import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle, Clock, Shield, Phone, ArrowRight, Star } from 'lucide-react';
import { MOCK_SERVICES } from '../../mock/services.mock';
import { APP_CONFIG, ROUTES } from '../../config/app.config';

const ServiceDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = MOCK_SERVICES.find(s => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const related = MOCK_SERVICES.filter(s => s.id !== service.id).slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <div style={{ position: 'relative', height: '400px', overflow: 'hidden' }}>
        <img src={service.image} alt={service.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(2,6,23,0.9) 0%, rgba(2,6,23,0.5) 100%)' }} />
        <div className="container-xl" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 1.5rem' }}>
          <div style={{ paddingTop: '4rem', maxWidth: '600px' }}>
            <Link to="/services" style={{ color: '#f97316', fontSize: '0.85rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.375rem', marginBottom: '1rem' }}>
              ← All Services
            </Link>
            {service.popular && <div style={{ marginBottom: '0.75rem' }}><span className="badge badge-orange">Most Popular</span></div>}
            <h1 style={{ color: 'white', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, marginBottom: '0.75rem' }}>{service.title}</h1>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#94a3b8', fontSize: '0.875rem' }}>
                <Clock size={14} /> {service.duration}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#94a3b8', fontSize: '0.875rem' }}>
                <Shield size={14} /> {service.warranty}
              </span>
              <span style={{ color: '#f97316', fontSize: '1.1rem', fontWeight: 800 }}>{service.price}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container-xl" style={{ padding: '3rem 1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '3rem', alignItems: 'start' }}>
          {/* Main Content */}
          <div>
            <h2 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>About This Service</h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: '1rem', marginBottom: '2rem' }}>{service.description}</p>

            <h3 style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>What's Included</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '2.5rem' }}>
              {service.features.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <CheckCircle size={18} color="#10b981" />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{f}</span>
                </div>
              ))}
            </div>

            <div className="card" style={{ background: 'linear-gradient(135deg, rgb(249 115 22 / 0.05), rgb(234 88 12 / 0.03))', border: '1px solid rgb(249 115 22 / 0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <Shield size={22} color="#f97316" />
                <h4 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Warranty Protection</h4>
              </div>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>
                This service includes our <strong style={{ color: '#f97316' }}>{service.warranty}</strong> — fully transferable and backed by {APP_CONFIG.name}.
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', position: 'sticky', top: '5rem' }}>
            <div className="card">
              <h3 style={{ fontWeight: 700, marginBottom: '1.25rem', color: 'var(--text-primary)' }}>Get Your Free Quote</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                <input className="input-field" placeholder="Your Name" />
                <input className="input-field" placeholder="Phone Number" />
                <input className="input-field" placeholder="Property Address" />
                <textarea className="input-field" placeholder="Additional details..." rows={3} style={{ resize: 'vertical' }} />
                <Link to={ROUTES.QUOTE} className="btn-primary" style={{ textAlign: 'center' }}>
                  Request Free Estimate <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            <div className="card">
              <p style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: '0.9rem' }}>Prefer to talk?</p>
              <a href={`tel:${APP_CONFIG.phoneRaw}`} className="btn-secondary" style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Phone size={16} /> {APP_CONFIG.phone}
              </a>
            </div>

            <div className="card" style={{ background: 'var(--bg-secondary)' }}>
              <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem' }}>
                {[1,2,3,4,5].map(i => <Star key={i} size={14} fill="#f97316" stroke="none" />)}
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontStyle: 'italic', marginBottom: '0.75rem' }}>
                "I can't believe the difference in our roof. The team was professional from start to finish."
              </p>
              <p style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--text-primary)' }}>— Jennifer W., Nashville</p>
            </div>
          </div>
        </div>

        {/* Related */}
        <div style={{ marginTop: '4rem' }}>
          <h2 style={{ fontWeight: 800, fontSize: '1.25rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Other Services You May Need</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.25rem' }}>
            {related.map(s => (
              <Link key={s.id} to={`/services/${s.slug}`} className="card card-hover" style={{ textDecoration: 'none' }}>
                <img src={s.image} alt={s.title} style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '0.625rem', marginBottom: '1rem' }} />
                <h3 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.375rem', color: 'var(--text-primary)' }}>{s.title}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', lineHeight: 1.5, marginBottom: '0.75rem' }}>{s.shortDesc}</p>
                <span style={{ color: '#f97316', fontWeight: 700, fontSize: '0.9rem' }}>{s.price}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 2fr 1fr"]{grid-template-columns:1fr!important}div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
};

export default ServiceDetailPage;
